/**
 * download-util.ts — the editor's single browser-download primitive.
 *
 * The Blob → object-URL → anchor-click idiom was inline in
 * `editor-modals-save.ts:downloadEditedFile` (the "Download edited PDF" action).
 * The "Export as ground truth" action (ground-truth-authoring Epic Phase 3) is a
 * SIBLING download differing only in payload — so the idiom is extracted here on
 * its second site (rule #11 / A.6) and BOTH actions call it rather than each
 * hand-rolling the anchor dance.
 *
 * DOM-free at module scope (functions only, no `getRequired`), so unit tests can
 * import it without the full editor DOM scaffold; the browser globals it uses
 * (`document`, `URL`) are reached lazily inside the default shim.
 *
 * @module editor/download-util
 */
/**
 * Download `blob` to the user's machine as `filename`.
 *
 * Mints an object URL, triggers the download (default: a transient `<a download>`
 * clicked programmatically), then revokes the URL in a `finally` so a failing
 * trigger never leaks the URL.
 */
export function triggerBlobDownload(blob, filename, shim) {
    const createObjectUrl = shim?.createObjectUrl ?? ((b) => URL.createObjectURL(b));
    const revokeObjectUrl = shim?.revokeObjectUrl ?? ((u) => URL.revokeObjectURL(u));
    const triggerDownload = shim?.triggerDownload ?? ((url, name) => {
        const a = document.createElement("a"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        a.href = url;
        a.download = name;
        document.body.appendChild(a); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        a.click();
        document.body.removeChild(a); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    });
    const url = createObjectUrl(blob);
    try {
        triggerDownload(url, filename);
    }
    finally {
        revokeObjectUrl(url);
    }
}
